import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.origin.includes('railway') || window.location.origin.includes('futureproofindia.com') ? window.location.origin : 'http://localhost:8000');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface ProcessResumeRequest {
  resume_text: string;
  job_description: string;
  include_resume_tweaker: boolean;
  include_gap_analysis: boolean;
  include_interview_questions: boolean;
}

export interface ProcessResumeResponse {
  status: string;
  results: {
    resume_tweaker?: any;
    skill_gap_analysis?: any;
    interview_questions?: any;
  };
  error?: string;
}

export interface PDFUploadResponse {
  success: boolean;
  extracted_text: string;
  page_count: number;
  error?: string;
}

export interface ComprehensiveAnalysisResponse {
  status: string;
  results: {
    resume_tweaker?: any;
    skill_gap_analysis?: any;
    interview_questions?: any;
  };
  summary: string;
  recommendations: string[];
  pdf_report_path?: string;
  error?: string;
}

export const processResume = async (request: ProcessResumeRequest): Promise<ProcessResumeResponse> => {
  try {
    const response = await api.post('/api/process-all', request);
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.detail || 'API request failed');
    } else if (error.request) {
      throw new Error('Unable to connect to the server. Please make sure the backend is running.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

export const uploadPDF = async (file: File): Promise<PDFUploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/api/upload-pdf', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.detail || 'PDF upload failed');
    } else if (error.request) {
      throw new Error('Unable to connect to the server. Please make sure the backend is running.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

export const comprehensiveAnalysis = async (request: ProcessResumeRequest): Promise<ComprehensiveAnalysisResponse> => {
  try {
    const response = await api.post('/api/comprehensive-analysis', request);
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.detail || 'Comprehensive analysis failed');
    } else if (error.request) {
      throw new Error('Unable to connect to the server. Please make sure the backend is running.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

export const downloadReport = async (filename: string): Promise<Blob> => {
  try {
    const response = await api.get(`/api/download-report/${filename}`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.detail || 'Report download failed');
    } else if (error.request) {
      throw new Error('Unable to connect to the server. Please make sure the backend is running.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

export const downloadAgentPDF = async (agentType: string, resumeText: string, jobDescription: string): Promise<Blob> => {
  try {
    const request = {
      resume_text: resumeText,
      job_description: jobDescription
    };
    
    const response = await api.post(`/api/${agentType}/download-pdf`, request, {
      responseType: 'blob',
    });
    
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.detail || 'PDF download failed');
    } else if (error.request) {
      throw new Error('Unable to connect to the server. Please make sure the backend is running.');
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

export const healthCheck = async (): Promise<boolean> => {
  try {
    const response = await api.get('/health');
    return response.data.status === 'healthy';
  } catch (error) {
    return false;
  }
};

export default api;
